/**
 * bridge/permissions_controller.js — "permissions" bridge component (web side).
 *
 * Identifier: bridge--permissions (glob-derived from the bridge/ subdir; wire with
 * data-controller="bridge--permissions"). static component = "permissions" MUST
 * equal the iOS class var name (native/ios/Chippie/Bridge/PermissionsComponent.swift)
 * and the Android-reached method names.
 *
 * What it does: query (check) and request native permission status — camera,
 * location, push, contacts, microphone — so web can verify a grant before
 * invoking a feature. Result is delivered to a callback as { permission, code,
 * text } where code is 1 authorized / 0 denied / -1 not determined / -2 unknown.
 *
 * Capability tier: extends the Strada BridgeComponent (so it gets `this.send` and
 * only connects inside the iOS Hotwire Native shell), and on connect it
 * contributes the `permissions` implementation triple to the window.ChippieBridge
 * facade ({ ios, android, pwa }). The facade owns transport detection; callers do:
 *
 *   window.ChippieBridge.permissions("check",   "microphone", (s) => { ... })
 *   window.ChippieBridge.permissions("request", "camera",     (s) => { ... })
 *
 * where `s` is { permission, code, text }.
 *
 *   - ios:     this.send("check"/"request", { permission }, reply) over Strada to
 *              PermissionsComponent.swift; the reply's message.data is the status.
 *   - android: window.ChippieNative.permissionStatus(action, permission, token) —
 *              async; the bridge calls back via a window-scoped resolver keyed by
 *              token (Android has no Strada reply channel). See registerStandalone.
 *   - pwa:     navigator.permissions.query / getUserMedia / Notification, mapped to
 *              the same { code, text } shape; unsupported permissions reply unknown.
 *
 * The Android + PWA impls are platform-static (no per-instance bridge state) so
 * they register once at module load; connect() only swaps in the live iOS send.
 * Mirrors the haptic controller's standalone-registration pattern.
 */
import { BridgeComponent } from "@hotwired/hotwire-native-bridge";

// Canonical status codes (match iOS/Android).
const CODE = { authorized: 1, denied: 0, notDetermined: -1, unknown: -2 };
const TEXT = {
    [CODE.authorized]: "authorized",
    [CODE.denied]: "denied",
    [CODE.notDetermined]: "not determined",
    [CODE.unknown]: "unknown",
};

// PWA: map a permission name to a { code, text } status using Web APIs. Best
// effort — desktop/unsupported browsers fall through to unknown.
function pwaCheck(permission, callback) {
    const done = (code) => callback({ permission, code, text: TEXT[code] });

    // navigator.permissions.query covers camera/microphone/geolocation/notifications
    // on most modern browsers without prompting.
    const QUERY_NAMES = {
        camera: "camera",
        microphone: "microphone",
        location: "geolocation",
        push: "notifications",
    };
    const queryName = QUERY_NAMES[permission];

    if (permission === "push" && typeof Notification !== "undefined") {
        const p = Notification.permission;
        return done(p === "granted" ? CODE.authorized : p === "denied" ? CODE.denied : CODE.notDetermined);
    }
    if (queryName && navigator.permissions && navigator.permissions.query) {
        navigator.permissions.query({ name: queryName }).then(
            (res) => done(res.state === "granted" ? CODE.authorized
                : res.state === "denied" ? CODE.denied : CODE.notDetermined),
            () => done(CODE.unknown)
        );
        return;
    }
    // contacts has no widely-supported web permission API.
    done(CODE.unknown);
}

// PWA: requesting actually prompts. Reuse check for the query-only permissions and
// trigger the real prompt for push (the only broadly-supported web request).
function pwaRequest(permission, callback) {
    const done = (code) => callback({ permission, code, text: TEXT[code] });
    if (permission === "push" && typeof Notification !== "undefined" && Notification.requestPermission) {
        Notification.requestPermission().then(
            (p) => done(p === "granted" ? CODE.authorized : p === "denied" ? CODE.denied : CODE.notDetermined),
            () => done(CODE.unknown)
        );
        return;
    }
    // Web has no neutral "request" for camera/mic/location without starting capture;
    // fall back to a status read so callers still get a usable answer.
    pwaCheck(permission, callback);
}

// Android: the @JavascriptInterface cannot return async results, so it resolves via
// a window-scoped registry keyed by a one-shot token. The bridge calls
// window.ChippiePermissionsResolve(token, json) when the OS responds.
function androidCall(action, permission, callback) {
    if (!window.ChippieNative || !window.ChippieNative.permissionStatus) {
        callback({ permission, code: CODE.unknown, text: TEXT[CODE.unknown] });
        return;
    }
    const registry = (window.__chippiePermissionResolvers ||= {});
    if (!window.ChippiePermissionsResolve) {
        window.ChippiePermissionsResolve = (token, json) => {
            const cb = registry[token];
            if (!cb) return;
            delete registry[token];
            let payload;
            try { payload = JSON.parse(json); } catch (_e) { payload = { code: CODE.unknown, text: TEXT[CODE.unknown] }; }
            cb(payload);
        };
    }
    const token = `perm_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    registry[token] = callback;
    try {
        window.ChippieNative.permissionStatus(action, permission, token);
    } catch (_e) {
        delete registry[token];
        callback({ permission, code: CODE.unknown, text: TEXT[CODE.unknown] });
    }
}

// Register the Android + PWA impls (and a safe iOS placeholder). The facade method
// is window.ChippieBridge.permissions(action, permission, callback).
function registerStandalone() {
    if (!window.ChippieBridge || !window.ChippieBridge.register) return;
    window.ChippieBridge.register("permissions", {
        // Overridden by connect() with a real Strada send inside the iOS shell.
        ios: (action, permission, callback) => callback &&
            callback({ permission, code: CODE.unknown, text: TEXT[CODE.unknown] }),
        android: (action, permission, callback) => androidCall(action, permission, callback || (() => {})),
        pwa: (action, permission, callback) => {
            const cb = callback || (() => {});
            (action === "request" ? pwaRequest : pwaCheck)(permission, cb);
        },
    });
}

if (window.ChippieBridge) {
    registerStandalone();
} else {
    document.addEventListener("DOMContentLoaded", registerStandalone, { once: true });
}

export default class extends BridgeComponent {
    static component = "permissions";

    connect() {
        super.connect();
        // Inside the iOS shell: wire the real Strada send into the facade, keeping
        // the Android/PWA fallbacks intact.
        if (!window.ChippieBridge || !window.ChippieBridge.register) return;
        window.ChippieBridge.register("permissions", {
            ios: (action, permission, callback) => {
                const event = action === "request" ? "request" : "check";
                this.send(event, { permission }, (message) => {
                    if (callback) callback(message.data);
                });
            },
            android: (action, permission, callback) => androidCall(action, permission, callback || (() => {})),
            pwa: (action, permission, callback) => {
                const cb = callback || (() => {});
                (action === "request" ? pwaRequest : pwaCheck)(permission, cb);
            },
        });
    }

    disconnect() {
        // Leaving the iOS shell context: drop the live-send impl back to the
        // standalone placeholder so a stale `this.send` is never invoked.
        registerStandalone();
        super.disconnect();
    }
}
